import React, { memo, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import classnames from 'classnames';
import { createUseStyles, Icon } from '@Sage/hoi-poi-ui';
import FullModalBlank from "../FullModalBlank";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'CarouselFullModal');
const CarouselFullModal = _ref => {
  let {
    isOpen,
    className,
    modalOverlayClassName,
    headerClass,
    contentClass,
    contentLeftClass,
    contentCenterClass,
    contentRightClass,
    onAfterOpen,
    onRequestClose,
    withCloseIcon,
    shouldCloseOnEsc,
    shouldCloseOnOverlayClick,
    contentLeft,
    contentCenter,
    contentRight,
    imageSelected,
    updateImageSelected,
    arrows = false,
    dots = false,
    //navigation dots
    lazyLoad = 'ondemand',
    // 'progressive' is the other option
    infinite = true,
    speed = 500,
    slidesToShow = 1,
    slidesToScroll = 1,
    draggable = false,
    //leave it false cause it causes wierd behaviour
    centerMode = false,
    //center current slide
    accessibility = true,
    initialSlide = null,
    images = []
  } = _ref;
  const classes = useStyles();
  // Classes
  const rootClassNames = classnames(classes.root, className);
  const modalOverlayClassNames = classnames(classes.overlay, modalOverlayClassName);
  const headerClassNames = classnames(classes.header, headerClass);
  const contentClassNames = classnames(classes.content, contentClass);
  const contentLeftClassNames = classnames(classes.contentLeft, contentLeftClass);
  const contentCenterClassNames = classnames(classes.contentCenter, contentCenterClass);
  const contentRightClassNames = classnames(classes.contentRight, contentRightClass);
  const sliderRef = useRef(null);
  const beforeChange = useCallback((current, next) => {
    updateImageSelected(next);
  }, [updateImageSelected]);
  const onClickPrevious = useCallback(() => {
    var _sliderRef$current, _sliderRef$current$sl;
    sliderRef === null || sliderRef === void 0 ? void 0 : (_sliderRef$current = sliderRef.current) === null || _sliderRef$current === void 0 ? void 0 : (_sliderRef$current$sl = _sliderRef$current.slickPrev) === null || _sliderRef$current$sl === void 0 ? void 0 : _sliderRef$current$sl.call(_sliderRef$current);
  }, []);
  const onClickNext = useCallback(() => {
    var _sliderRef$current2, _sliderRef$current2$s;
    sliderRef === null || sliderRef === void 0 ? void 0 : (_sliderRef$current2 = sliderRef.current) === null || _sliderRef$current2 === void 0 ? void 0 : (_sliderRef$current2$s = _sliderRef$current2.slickNext) === null || _sliderRef$current2$s === void 0 ? void 0 : _sliderRef$current2$s.call(_sliderRef$current2);
  }, []);
  const renderImages = useMemo(() => {
    if (!(images !== null && images !== void 0 && images.length)) return null;
    return images.map((image, index) => /*#__PURE__*/React.createElement("div", {
      key: index,
      className: classes.sliderImage
    }, /*#__PURE__*/React.createElement("img", {
      src: image.strLink,
      alt: image.description
    })));
  }, [classes.sliderImage, images]);
  if (!(images !== null && images !== void 0 && images.length) || imageSelected < 0 || !updateImageSelected) return null;
  const settings = {
    className: classes.slider,
    arrows,
    dots,
    lazyLoad,
    infinite,
    speed,
    slidesToShow,
    slidesToScroll,
    centerMode,
    draggable,
    initialSlide: initialSlide !== null ? initialSlide : imageSelected,
    accessibility,
    beforeChange,
    ref: slider => sliderRef.current = slider
  };
  return /*#__PURE__*/React.createElement(FullModalBlank, {
    className: rootClassNames,
    overlayClassName: modalOverlayClassNames,
    headerClass: headerClassNames,
    isOpen: isOpen,
    onAfterOpen: onAfterOpen,
    withCloseIcon: withCloseIcon,
    onRequestClose: onRequestClose,
    shoudCloseOnEsc: shouldCloseOnEsc,
    shouldCloseOnOverlayClick: shouldCloseOnOverlayClick,
    contentLeftClassName: contentLeftClassNames,
    contentCenterClassName: contentCenterClassNames,
    contentRightClassName: contentRightClassNames,
    contentLeft: contentLeft,
    contentCenter: contentCenter,
    contentRight: contentRight
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.contentBackground
  }), /*#__PURE__*/React.createElement("div", {
    className: contentClassNames
  }, /*#__PURE__*/React.createElement(Slider, settings, renderImages), /*#__PURE__*/React.createElement("div", {
    className: classes.previousButton,
    onClick: onClickPrevious
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.previousButtonContent
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "chevron",
    size: "big",
    color: "white",
    className: classes.chevronLeft
  }))), /*#__PURE__*/React.createElement("div", {
    className: classes.nextButton,
    onClick: onClickNext
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.nextButtonContent
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "chevron",
    size: "big",
    color: "white",
    className: classes.chevronRight
  })))));
};
process.env.NODE_ENV !== "production" ? CarouselFullModal.propTypes = {
  //FullModal parameters
  isOpen: PropTypes.bool,
  modalClassName: PropTypes.string,
  modalOverlayClassName: PropTypes.string,
  headerClass: PropTypes.string,
  contentClass: PropTypes.string,
  contentLeftClass: PropTypes.string,
  contentCenterClass: PropTypes.string,
  contentRightClass: PropTypes.string,
  onAfterOpen: PropTypes.func,
  onRequestClose: PropTypes.func,
  withCloseIcon: PropTypes.bool,
  shouldCloseOnEsc: PropTypes.bool,
  shouldCloseOnOverlayClick: PropTypes.bool,
  contentLeft: PropTypes.func,
  contentCenter: PropTypes.func,
  contentRight: PropTypes.func,
  //Carousel parameters
  arrows: PropTypes.bool,
  dots: PropTypes.bool,
  lazyLoad: PropTypes.string,
  infinite: PropTypes.bool,
  speed: PropTypes.number,
  slidesToShow: PropTypes.number,
  slidesToScroll: PropTypes.number,
  draggable: PropTypes.bool,
  centerMode: PropTypes.bool,
  initialSlide: PropTypes.number,
  accessibility: PropTypes.bool,
  //Images
  images: PropTypes.array,
  imageSelected: PropTypes.number,
  updateImageSelected: PropTypes.func
} : void 0;
export default /*#__PURE__*/memo(CarouselFullModal);